import Fuse from "fuse.js";

export type CatalogCredit = {
  artistId: string;
  artistName: string;
  role: string;
};

export type CatalogCreditRoleGroup = {
  credits: CatalogCredit[];
  key: string;
  role: string;
};

export type CatalogCompanyRelationship = {
  companyId: string | null;
  companyName: string;
  role: string;
};

export type CatalogCompanyRoleGroup = {
  companies: CatalogCompanyRelationship[];
  key: string;
  role: string;
};

export type CatalogCreditsCompaniesSearchRecord = {
  key: string;
  kind: "credit" | "company";
  searchText: string;
};

export function groupCatalogCreditsByRole(
  credits: CatalogCredit[] | null | undefined,
): CatalogCreditRoleGroup[] {
  const groups = new Map<string, CatalogCreditRoleGroup>();
  const seenCredits = new Set<string>();

  for (const credit of credits ?? []) {
    if (!credit.artistName || !credit.role) continue;
    const creditKey = `${credit.role}:${credit.artistId || normalizeCatalogCreditsCompaniesSearch(credit.artistName)}`;
    if (seenCredits.has(creditKey)) continue;
    seenCredits.add(creditKey);

    const existing = groups.get(credit.role);
    if (existing) {
      existing.credits.push(credit);
      continue;
    }

    groups.set(credit.role, {
      credits: [credit],
      key: `role:${credit.role}`,
      role: credit.role,
    });
  }

  return [...groups.values()];
}

export function groupCatalogCompaniesByRole(
  companies: CatalogCompanyRelationship[] | null | undefined,
): CatalogCompanyRoleGroup[] {
  const groups = new Map<string, CatalogCompanyRoleGroup>();
  const seenRelationships = new Set<string>();

  for (const company of companies ?? []) {
    if (!company.companyName || !company.role) continue;
    const normalizedName = normalizeCatalogCreditsCompaniesSearch(company.companyName);
    const companyKey = company.companyId ? `id:${company.companyId}` : `name:${normalizedName}`;
    const relationshipKey = `${company.role}:${companyKey}`;
    if (seenRelationships.has(relationshipKey)) continue;
    seenRelationships.add(relationshipKey);

    const existing = groups.get(company.role);

    if (existing) {
      existing.companies.push(company);
      continue;
    }

    groups.set(company.role, {
      companies: [company],
      key: `role:${company.role}`,
      role: company.role,
    });
  }

  return [...groups.values()];
}

export function searchCatalogCreditsCompaniesRecords<T extends CatalogCreditsCompaniesSearchRecord>(
  records: T[],
  query: string,
): T[] {
  const normalizedQuery = normalizeCatalogCreditsCompaniesSearch(query);
  if (!normalizedQuery) return records;

  return new Fuse(records, {
    keys: ["searchText"],
    threshold: 0.3,
    ignoreLocation: true,
    minMatchCharLength: 1,
  })
    .search(normalizedQuery)
    .map((result) => result.item);
}

export function normalizeCatalogCreditsCompaniesSearch(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .trim();
}
